import { SerializedStyles } from '@emotion/react'
import { css, useTheme } from '@mui/material'
import Link from 'next/link'
import React from 'react'
import { unifiedColors } from 'styles/colors'
import { Theme, themeUtils } from 'styles/theme'
import { LinkButtonVariant } from './LinkButtonVariant'

interface LinkWrapperProps {
  children: React.ReactNode
  noFollow?: boolean
  href: string
  variation: LinkButtonVariant
}
interface LinkButtonProps extends LinkWrapperProps {
  variationColor?: string
  fontVariationColor?: string
  hoverVariationColor?: string
  style?: SerializedStyles
}

const LinkWrapper = ({
  children,
  href,
  noFollow,
  variation
}: LinkWrapperProps) => {
  return variation !== LinkButtonVariant.DISABLE ? (
    href.startsWith('/') ? (
      <Link
        href={href}
        passHref
        prefetch={false}
        css={css`
          text-decoration: none;
          &:active {
            text-decoration: none;
          }
        `}
      >
        {children}
      </Link>
    ) : (
      <a
        css={css`
          text-decoration: none;
        `}
        href={href}
        target="_blank"
        rel={`noopener noreferrer${noFollow ? ' nofollow' : ''}`}
        aria-label={href}
      >
        {children}
      </a>
    )
  ) : (
    <>{children}</>
  )
}

export const LegacyLinkButton = ({
  children,
  href,
  variation,
  variationColor,
  style,
  hoverVariationColor,
  fontVariationColor,
  noFollow
}: LinkButtonProps) => {
  const theme: Theme = useTheme()
  const color = variationColor ? variationColor : theme.palette.surface.default
  const hoverColor = hoverVariationColor
    ? hoverVariationColor
    : unifiedColors.button.primary.hovered
  const fontColor = fontVariationColor ? fontVariationColor : color
  return (
    <LinkWrapper href={href} noFollow={noFollow} variation={variation}>
      <div
        css={css`
          box-sizing: border-box;
          border-radius: 12px;
          padding: 16px 20px;
          cursor: pointer;
          white-space: nowrap;
          color: ${fontColor};
          text-decoration: none;
          ${variation === LinkButtonVariant.OUTLINED
            ? themeUtils.label
            : themeUtils.button};
          ${variation === LinkButtonVariant.OUTLINED &&
          `border: solid 2px ${color};`}
          background-color: ${variation === LinkButtonVariant.OUTLINED
            ? 'transparent'
            : color};
          &:hover {
            ${themeUtils.minWidth.md} {
              ${variation === LinkButtonVariant.OUTLINED &&
              `border: solid 2px ${hoverColor};
                color: ${hoverColor};`}
              transform: scale(1.02);
            }
          }
          ${themeUtils.maxWidth.sm} {
            padding: 12px 16px;
          }
          ${variation === LinkButtonVariant.DISABLE &&
          `cursor: not-allowed;
          color: ${theme.palette.surface.secondary};
          background-color: ${theme.palette.surface.default};
          border: 1px solid ${theme.palette.surface.secondary};
          div path {
            fill: ${theme.palette.surface.secondary};
          }`};
          ${style}
        `}
      >
        {children}
      </div>
    </LinkWrapper>
  )
}
